// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";

export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    acceptGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    closeGridItem?: GridItemProps;
    closeButton?: ButtonPresentationProps;
    // XNG-Changes
    page?: MobiusPageProps;
}

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        size: 100,
        isAutoSize: true,
        cssOverrides: {
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                padding: 15px;
                margin: 0 auto;
                width: 100%;
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    container: {
        gap: 10,
        css: css`
            margin: 0 auto;
        `,
    },
    messageGridItem: {
        width: [12, 12, 7, 9, 9],
    },
    acceptGridItem: {
        width: [8, 9, 3, 2, 2],
    },
    acceptButton: {
        css: css`
            width: 100%;
        `,
    },
    closeGridItem: {
        width: [4, 3, 2, 1, 1],
    },
    closeButton: {
        css: css`
            width: 100%;
        `,
        variant: "secondary",
    },
};

const styles = cookiePrivacyPolicyPopupStyles;

const CookiePrivacyPolicyPopup = () => {
    const location = useAppSelector(o => getLocation(o));
    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie("acceptCookies")) {
            setIsOpen(true);
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setIsOpen(false);
        setCookie("acceptCookies", "true", { expires: 365 });
    };

    const closeHandler = () => {
        setIsOpen(false);
    };

    // XNG-Changes: Update Structure
    return (
        <Drawer isOpen={isOpen} {...styles.drawer} data-test-selector="drawer_CookiePolicy">
            <Page {...styles.page}>
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.messageGridItem}>
                        <Typography {...styles.messageText} data-test-selector="cookiePolicy_PrivacyPolicy">
                            {siteMessage("Core_CookiePrivacyPolicy")}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.acceptGridItem}>
                        <Button
                            {...styles.acceptButton}
                            onClick={acceptHandler}
                            data-test-selector="cookiePolicy_AcceptCookies"
                        >
                            {siteMessage("Core_CookiePrivacyPolicyAccept")}
                        </Button>
                        <Button
                            {...styles.closeButton}
                            onClick={closeHandler}
                            aria-label={translate("Close")}
                            data-test-selector="cookiePolicy_ClosePolicy"
                        >
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Page>
        </Drawer>
    );
};

export default CookiePrivacyPolicyPopup;
